import { useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

import { useAuthStore } from '../store/authStore';
import { useCommandStore } from '../store/commandStore';

const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:3001';

interface PositionUpdate {
  deviceId: string;
  imei: string;
  latitude: number;
  longitude: number;
  speed: number | null;
  heading: number | null;
  deviceTime: string;
  serverTime: string;
}

interface CommandResult {
  commandId: string;
  deviceId: string;
  type: string;
  status: 'PENDING' | 'SENT' | 'CONFIRMED' | 'FAILED' | 'EXPIRED';
  result?: string | null;
  error?: string | null;
}

let socket: Socket | null = null;

export function useSocket(deviceIds: string[] = []) {
  const accessToken = useAuthStore((state) => state.accessToken);
  const [isConnected, setIsConnected] = useState(false);
  const [lastPosition, setLastPosition] = useState<PositionUpdate | null>(null);

  useEffect(() => {
    if (!accessToken) return;

    // Create socket connection if not exists
    if (!socket) {
      socket = io(WS_URL, {
        auth: { token: accessToken },
        transports: ['websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 10,
      });

      socket.on('connect', () => {
        setIsConnected(true);
      });

      socket.on('disconnect', () => {
        setIsConnected(false);
      });

      socket.on('position:update', (data: PositionUpdate) => {
        setLastPosition(data);
      });

      socket.on('command:result', (data: CommandResult) => {
        // Route through the store so any component can read it, regardless of
        // which useSocket instance owns the singleton connection.
        useCommandStore.getState().setResult(data);
      });

      socket.on('error', (error) => {
        console.error('Socket error:', error);
      });
    }

    // Subscribe to devices
    deviceIds.forEach((deviceId) => {
      socket?.emit('subscribe:device', { deviceId });
    });

    // Cleanup: unsubscribe from devices
    return () => {
      deviceIds.forEach((deviceId) => {
        socket?.emit('unsubscribe:device', { deviceId });
      });
    };
  }, [accessToken, deviceIds.join(',')]);

  const subscribeToDevice = useCallback((deviceId: string) => {
    socket?.emit('subscribe:device', { deviceId });
  }, []);

  const unsubscribeFromDevice = useCallback((deviceId: string) => {
    socket?.emit('unsubscribe:device', { deviceId });
  }, []);

  return {
    isConnected,
    lastPosition,
    subscribeToDevice,
    unsubscribeFromDevice,
  };
}
